import { SiteInformation } from '@ta-interaktiv/types/lib/admin.publishing.tamedia.ch/admin-api/v1/SiteInformation'
import { getPropertyFromPromisedObject } from './getSingleProperty'
import { tenantSiteInfos } from "./tenantSiteInfos"


/** Fallback value in case where we got a fantasy domain name, or otherwise received an error. */
const defaultSiteInformation: SiteInformation = {
  key: 'tagesanzeiger',
  abbr: 'ta',
  name: 'Tages-Anzeiger',
  domain: 'tagesanzeiger',
  feedId: 2,
  lang: 'de',
  paywallServiceId: 'tagesanzeiger',
  fbAppId: '147124108642216',
  twitterHandle: '@tagesanzeiger'
}

const siteInformationPromise = new Map<string, Promise<SiteInformation>>()

/**
 * For testing purposes only – clears the cache.
 */
export function clearCache() {
  siteInformationPromise.clear()
}

/**
 * For testing purposes only – injects data into the cache
 */
export function injectDataInCache(
  domain: string,
  siteInformation: SiteInformation
) {
  siteInformationPromise.set(domain, Promise.resolve(siteInformation))
}

/**
 * Gets meta data of a paid media site based on the passed in domain name.
 *
 * The downloaded site information is stored in the
 * `window.project._siteInformation` property for later retrieval
 *
 * If it fails for whatever reason, a warning is logged in the
 * console, and default values (currently: from Tages-Anzeiger) are being used.
 *
 * @param domainname - Just the pure domain name, without the top level domain.
 */
export function getSiteInformation(
  domainname: string
): Promise<SiteInformation> {
  // Check if we already have the information, then get the information from there
  if (siteInformationPromise.has(domainname)) {
    return siteInformationPromise.get(domainname) as Promise<SiteInformation>
  }

  // Otherwise get the information from the static file "tenantSiteInfos.ts"
 
  const foundSiteInfo = tenantSiteInfos.find(tenant => tenant.domain === domainname)  
  const promise = Promise.resolve(foundSiteInfo || defaultSiteInformation)

  siteInformationPromise.set(domainname, promise)

  return promise
}

/**
 * Get the predefined language of the domain.
 * @param domainName
 */
export function getLanguage(domainName: string) {
  return getPropertyFromPromisedObject(getSiteInformation(domainName), 'lang')
}

/**
 * Get the feed ID for the domain.
 * @param domainName
 */
export function getFeedId(domainName: string) {
  return getPropertyFromPromisedObject(getSiteInformation(domainName), 'feedId')
}

/**
 * Get the nicely formatted name for the domain.
 * @param domainName
 */
export function getNiceName(domainName: string) {
  return getPropertyFromPromisedObject(getSiteInformation(domainName), 'name')
}

/**
 * Get the Facebook App ID, used for sharing stuff on Facebook.
 * @param domainName
 */
export function getFacebookAppId(domainName: string) {
  return getPropertyFromPromisedObject(
    getSiteInformation(domainName),
    'fbAppId'
  )
}

/**
 * Get the main Twitter handle for a domain.
 * @param domainName
 */
export function getTwitterHandle(domainName: string) {
  return getPropertyFromPromisedObject(
    getSiteInformation(domainName),
    'twitterHandle'
  )
}
